import React from "react";
import { lazy } from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import ErrorPage from "../components/ErrorPage";
import AuthLayout from "../layouts/AuthLayout";
import MainLayout from "../layouts/MainLayout";
import ProtectedRoute from "../components/ProtectedRoute";
import HomeRedirect from "../components/HomeRedirect";
import { useRole } from "../contexts/RoleContext";

function StudentRoute() {
  const { role } = useRole();
  if (role === "tutor") return <Navigate to="/tutor/dashboard" replace />;
  return <Outlet />;
}

// Lazy Loaded Pages
const SignUp = lazy(() => import("../pages/auth/signup"));
const Login = lazy(() => import("../pages/auth/login"));
const ForgotPassword = lazy(() => import("../pages/auth/forgot-password"));
const ResetPassword = lazy(() => import("../pages/auth/reset-password"));
const RegisterVerify = lazy(() => import("../pages/auth/register-verify"));
const SetupPassword = lazy(() => import("../pages/auth/setup-password"));
const MembershipPlans = lazy(() =>
  import("../pages/auth/signup/membership-plans")
);
const MultiLearnerSetup = lazy(() =>
  import("../pages/auth/multi-learner-setup")
);
const Dashboard = lazy(() => import("../pages/student/dashboard"));
const MyCourses = lazy(() => import("../pages/student/my-courses"));
const Pricing = lazy(() => import("../pages/student/pricing"));
const PaymentSuccess = lazy(() => import("../pages/payment/success"));
const MyReceipts = lazy(() => import("../pages/student/my-receipts"));
const MyCalendar = lazy(() => import("../pages/student/my-calendar"));
const CourseLesson = lazy(() => import("../pages/student/course-lesson"));
const Settings = lazy(() => import("../pages/settings/index"));
const Account = lazy(() => import("../pages/settings/account"));
const Security = lazy(() => import("../pages/settings/security"));
const TutorDashboard = lazy(() => import("../pages/tutor/dashboard"));
const TutorMyCourses = lazy(() => import("../pages/tutor/my-courses"));
const TutorMyStudents = lazy(() => import("../pages/tutor/my-students"));
const TutorMyCalendar = lazy(() => import("../pages/tutor/my-calendar"));
const SubscriptionSuccess = lazy(() => import("../pages/subscription/success"));

const router = createBrowserRouter([
  // Pathless layout route: top-level paths reached via email links.
  // AuthLayout renders its own <Outlet /> internally, so children display correctly.
  {
    element: <AuthLayout />,
    errorElement: <ErrorPage />,
    children: [
      {
        path: "/register/verify",
        element: <RegisterVerify />,
      },
    ],
  },
  {
    path: "/auth",
    errorElement: <ErrorPage />,
    element: <AuthLayout />,
    children: [
      {
        path: "signup",
        element: <SignUp />,
      },
      {
        path: "login",
        element: <Login />,
      },
      {
        path: "forgot-password",
        element: <ForgotPassword />,
      },
      {
        path: "reset-password",
        element: <ResetPassword />,
      },
      {
        path: "setup-password",
        element: <SetupPassword />,
      },
      {
        path: "signup/membership-plans",
        element: <MembershipPlans />,
      },
      {
        path: "multi-learner-setup",
        element: <MultiLearnerSetup />,
      },
    ],
  },
  {
    path: "/",
    errorElement: <ErrorPage />,
    element: <ProtectedRoute />,
    children: [
      {
        element: (
          <MainLayout>
            <Outlet />
          </MainLayout>
        ),
        children: [
          {
            path: "",
            element: <HomeRedirect />,
          },
          // Student-only routes (tutors are redirected away)
          {
            element: <StudentRoute />,
            children: [
              {
                path: "dashboard",
                element: <Dashboard />,
              },
              {
                path: "my-courses",
                element: <MyCourses />,
              },
              {
                path: "pricing",
                element: <Pricing />,
              },
              {
                path: "my-receipts",
                element: <MyReceipts />,
              },
              {
                path: "my-calendar",
                element: <MyCalendar />,
              },
            ],
          },
          // Shared: course detail (role-aware content inside)
          {
            path: "course/:courseId",
            element: <CourseLesson />,
          },
          // Tutor routes
          {
            path: "tutor/dashboard",
            element: <TutorDashboard />,
          },
          {
            path: "tutor/my-courses",
            element: <TutorMyCourses />,
          },
          {
            path: "tutor/my-students",
            element: <TutorMyStudents />,
          },
          {
            path: "tutor/my-calendar",
            element: <TutorMyCalendar />,
          },
          // Subscription success (Stripe / Paystack redirect)
          {
            path: "subscription/success",
            element: <SubscriptionSuccess />,
          },
          // Payment success (Stripe / Paystack redirect)
          {
            path: "payment/success",
            element: <PaymentSuccess />,
          },
          // Settings (shared)
          {
            path: "settings",
            element: <Settings />,
            children: [
              {
                path: "account",
                element: <Account />,
              },
              {
                path: "security",
                element: <Security />,
              },
              {
                index: true,
                element: <Account />,
              },
            ],
          },
        ],
      },
    ],
  },
]);

export { router };
